import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import EmojiPicker, {
    EmojiStyle,
    Theme
} from 'emoji-picker-react';
import { useLocation } from 'react-router-dom';
import {
    Avatar,
    Badge,
    Button,
    Dropdown,
    Empty,
    Image,
    Input,
    Modal,
    Select,
    Space,
    Typography,
    message,
    Spin,
    Upload
} from 'antd';
import {
    MessageOutlined,
    SendOutlined,
    TeamOutlined,
    UserOutlined,
    SmileOutlined,
    SearchOutlined,
    CloseOutlined,
    CheckOutlined,
    GiftOutlined,
    UploadOutlined,
    PaperClipOutlined,
    FileOutlined,
    CloseCircleFilled,
    DownloadOutlined,
    EyeOutlined,
    EnterOutlined,
    FileAddOutlined,
    CompressOutlined,
    ExpandOutlined,
    DragOutlined,
    CameraOutlined
} from '@ant-design/icons';
import {
    playChatMessageSound,
    showBrowserNotification
} from '../utils/notificationAlerts';
import {
    STICKER_PACKS,
    findSticker,
    isAnimatedSticker
} from '../data/chatStickers';
import './EmployeeChatPage.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const sortMessagesAsc = list =>
    [...(list || [])].sort((a, b) => {
        const ia = Number(a?.id) || 0;
        const ib = Number(b?.id) || 0;
        if (ia !== ib) return ia - ib;
        return (
            new Date(a?.created_at || 0).getTime() -
            new Date(b?.created_at || 0).getTime()
        );
    });

const DEFAULT_REACTION_EMOJIS = [
    '👍',
    '❤️',
    '😂',
    '😮',
    '😢',
    '🔥',
    '👏',
    '✅'
];

const CHAT_UI_KEY = 'crm-chat-ui';
const CHAT_POPUP_Z = 45000;

const loadChatUi = () => {
    try {
        return JSON.parse(
            localStorage.getItem(CHAT_UI_KEY) || '{}'
        );
    } catch (_) {
        return {};
    }
};

const saveChatUi = patch => {
    const prev = loadChatUi();
    const next = { ...prev, ...patch };
    localStorage.setItem(CHAT_UI_KEY, JSON.stringify(next));
    return next;
};

const defaultMiniPos = () => ({
    x: Math.max(16, window.innerWidth - 460),
    y: Math.max(16, window.innerHeight - 700)
});

const defaultMiniSize = () => ({
    w: Math.min(440, window.innerWidth - 32),
    h: Math.min(640, window.innerHeight - 32)
});

const defaultFabPos = () => ({
    x: 18,
    y: Math.max(18, window.innerHeight - 72)
});

const MIN_PANEL_W = 360;
const MIN_PANEL_H = 420;

const messagePreviewText = msg => {
    if (!msg) return '';
    if (msg.deleted) return 'Сообщение удалено';
    if (msg.msg_type === 'sticker') return 'Стикер';
    if (msg.msg_type === 'image') {
        return msg.body ? `Фото: ${msg.body}` : 'Фото';
    }
    if (msg.msg_type === 'file') {
        return `Файл: ${msg.file?.name || 'вложение'}`;
    }
    return String(msg.body || '').trim();
};

const meFromStorage = () => {
    try {
        return JSON.parse(
            localStorage.getItem('user') || 'null'
        );
    } catch {
        return null;
    }
};

const initials = name =>
    String(name || '?')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(p => p[0])
        .join('')
        .toUpperCase();

const resolveAvatarSrc = (user, cacheKey) => {
    if (!user?.id) {
        return null;
    }

    const raw = String(user.avatar_url || '').trim();
    if (!raw) {
        return null;
    }

    if (/^https?:\/\//i.test(raw)) {
        return raw;
    }

    const base = raw.startsWith('/')
        ? raw.split('?')[0]
        : `/api/users/${user.id}/avatar-file`;
    const version =
        cacheKey ||
        raw.match(/[?&]v=(\d+)/)?.[1] ||
        Date.now();

    return `${base}?v=${version}`;
};

const patchStoredUserAvatar = avatarUrl => {
    try {
        const stored = meFromStorage();
        if (!stored) {
            return;
        }

        localStorage.setItem(
            'user',
            JSON.stringify({
                ...stored,
                avatar_url: avatarUrl
            })
        );
    } catch (_) {
        /* ignore */
    }
};

const StickerMedia = ({ sticker, className }) => {
    if (!sticker?.src) {
        return null;
    }

    if (isAnimatedSticker(sticker)) {
        return (
            <video
                className={className}
                src={sticker.src}
                autoPlay
                loop
                muted
                playsInline
            />
        );
    }

    return (
        <img
            className={className}
            src={sticker.src}
            alt={sticker.label || 'Стикер'}
        />
    );
};

const canUseStaffChat = roleId => {
    const id = Number(roleId);
    return id && id !== 4 && id !== 5;
};

const CHAT_STATUS_KEY = 'emp-chat-status';
const CHAT_STATUSES = [
    { id: 'online', label: 'Онлайн' },
    { id: 'work', label: 'В работе' },
    { id: 'busy', label: 'Занят' }
];

const ROLE_LABELS = {
    1: 'Администратор',
    2: 'Менеджер',
    3: 'Исполнитель',
    4: 'Заказчик',
    5: 'Монтажник',
    6: 'HR',
    7: 'Управление пользователями',
    8: 'Руководитель отдела персонала'
};

const readStoredStatus = () => {
    try {
        const s = localStorage.getItem(CHAT_STATUS_KEY);
        if (
            s === 'online' ||
            s === 'work' ||
            s === 'busy'
        ) {
            return s;
        }
    } catch {
        /* ignore */
    }
    return 'online';
};

const peerStatus = (map, userId) => {
    const id = Number(userId);
    return map[id] || map[String(id)] || 'online';
};

const statusText = (online, status) => {
    if (!online) return 'не в сети';
    if (status === 'work') return 'в работе';
    if (status === 'busy') return 'занят';
    return 'онлайн';
};

const statusDotClass = (online, status) => {
    if (!online) return 'emp-chat__online';
    if (status === 'work') {
        return 'emp-chat__online emp-chat__online--work';
    }
    if (status === 'busy') {
        return 'emp-chat__online emp-chat__online--busy';
    }
    return 'emp-chat__online emp-chat__online--on';
};

const MAX_CHAT_FILE_BYTES = 25 * 1024 * 1024;
const MAX_CHAT_FILES = 10;
const BLOCKED_FILE_RE =
    /\.(exe|bat|cmd|com|msi|scr|js|vbs|ps1)$/i;

const formatBytes = n => {
    const v = Number(n) || 0;
    if (v < 1024) return `${v} Б`;
    if (v < 1024 * 1024) {
        return `${(v / 1024).toFixed(1)} КБ`;
    }
    return `${(v / (1024 * 1024)).toFixed(1)} МБ`;
};

const messagePreview = msg => {
    if (!msg) return '';
    if (msg.msg_type === 'sticker') return '🖼 Стикер';
    if (msg.msg_type === 'image') {
        return msg.body ? `📷 ${msg.body}` : '📷 Фото';
    }
    if (msg.msg_type === 'file') {
        return `📎 ${msg.file?.name || msg.body || 'Файл'}`;
    }
    return msg.body || '';
};

const previewKind = file => {
    const mime = String(file?.mime || '').toLowerCase();
    const name = String(file?.name || '').toLowerCase();
    if (
        mime.startsWith('image/') ||
        /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name)
    ) {
        return 'image';
    }
    if (mime === 'application/pdf' || name.endsWith('.pdf')) {
        return 'pdf';
    }
    if (
        mime.startsWith('video/') ||
        /\.(mp4|webm|ogg|mov)$/i.test(name)
    ) {
        return 'video';
    }
    if (
        mime.startsWith('audio/') ||
        /\.(mp3|wav|ogg|m4a)$/i.test(name)
    ) {
        return 'audio';
    }
    if (
        mime.startsWith('text/') ||
        mime === 'application/json' ||
        /\.(txt|csv|json|md|log)$/i.test(name)
    ) {
        return 'text';
    }
    return 'other';
};

const ChatAuthImage = ({ url, alt, file, onOpen }) => {
    const [src, setSrc] = useState(null);

    useEffect(() => {
        let objectUrl;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(url, {
                    headers: authHeaders()
                });
                if (!res.ok) return;
                const blob = await res.blob();
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            } catch {
                /* ignore */
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [url]);

    if (!src) {
        return (
            <div className="emp-chat__msg-image-loading">
                <Spin size="small" />
            </div>
        );
    }

    return (
        <button
            type="button"
            className="emp-chat__msg-image-hit"
            onClick={() =>
                onOpen?.(
                    file || {
                        url,
                        name: alt || 'Скриншот',
                        mime: 'image/png'
                    }
                )
            }
            title="Открыть"
        >
            <img
                className="emp-chat__msg-image"
                src={src}
                alt={alt || 'Фото'}
            />
        </button>
    );
};

const downloadAuthFile = async (url, name) => {
    const sep = url.includes('?') ? '&' : '?';
    const res = await fetch(`${url}${sep}download=1`, {
        headers: authHeaders()
    });
    if (!res.ok) {
        throw new Error('Не удалось скачать файл');
    }
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = name || 'file';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
};

const ChatFilePreview = ({ file, onClose }) => {
    const kind = previewKind(file);
    const [blobUrl, setBlobUrl] = useState(null);
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(kind !== 'other');

    useEffect(() => {
        if (kind === 'other' || !file?.url) {
            setLoading(false);
            return undefined;
        }
        const url = String(file.url);
        if (
            url.startsWith('blob:') ||
            url.startsWith('data:')
        ) {
            setBlobUrl(url);
            setLoading(false);
            return undefined;
        }
        let objectUrl;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(file.url, {
                    headers: authHeaders()
                });
                if (!res.ok) throw new Error('load');
                const blob = await res.blob();
                if (cancelled) return;
                const typed = new Blob([blob], {
                    type: file.mime || blob.type || ''
                });
                if (kind === 'text') {
                    setText(await typed.text());
                } else {
                    objectUrl = URL.createObjectURL(typed);
                    setBlobUrl(objectUrl);
                }
            } catch {
                if (!cancelled) {
                    message.error(
                        'Не удалось открыть файл'
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [file?.url, file?.mime, kind]);

    return (
        <Modal
            title={file?.name || 'Файл'}
            open
            onCancel={onClose}
            width={
                kind === 'other' || kind === 'audio'
                    ? 560
                    : '90vw'
            }
            centered
            zIndex={32000}
            getContainer={() => document.body}
            footer={[
                file?.url &&
                !String(file.url).startsWith('blob:') &&
                !String(file.url).startsWith('data:') ? (
                    <Button
                        key="dl"
                        icon={<DownloadOutlined />}
                        onClick={() =>
                            downloadAuthFile(
                                file.url,
                                file.name
                            ).catch(() =>
                                message.error(
                                    'Не удалось скачать файл'
                                )
                            )
                        }
                    >
                        Скачать
                    </Button>
                ) : null,
                <Button key="close" onClick={onClose}>
                    Закрыть
                </Button>
            ]}
        >
            {loading ? (
                <div className="emp-chat__file-preview-loading">
                    <Spin />
                </div>
            ) : kind === 'pdf' && blobUrl ? (
                <iframe
                    className="emp-chat__file-preview-frame"
                    title={file.name}
                    src={blobUrl}
                />
            ) : kind === 'image' && blobUrl ? (
                <img
                    className="emp-chat__file-preview-img"
                    src={blobUrl}
                    alt={file.name}
                />
            ) : kind === 'video' && blobUrl ? (
                <video
                    className="emp-chat__file-preview-media"
                    src={blobUrl}
                    controls
                    autoPlay
                />
            ) : kind === 'audio' && blobUrl ? (
                <audio src={blobUrl} controls autoPlay />
            ) : kind === 'text' ? (
                <pre className="emp-chat__file-preview-text">
                    {text}
                </pre>
            ) : (
                <div className="emp-chat__file-preview-empty">
                    <FileOutlined />
                    <div>{file?.name}</div>
                    <div>
                        Предпросмотр этого типа недоступен —
                        скачайте файл.
                    </div>
                </div>
            )}
        </Modal>
    );
};

const EmployeeChatWidget = () => {
    const location = useLocation();
    const me = meFromStorage();
    const meId = Number(me?.id);

    const [open, setOpen] = useState(false);
    const [hasAlert, setHasAlert] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [directory, setDirectory] = useState([]);
    const [activeRoomId, setActiveRoomId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState('');
    const [loadingRooms, setLoadingRooms] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sending, setSending] = useState(false);
    const [search, setSearch] = useState('');
    const [pickerTab, setPickerTab] = useState(null);
    const [activePackId, setActivePackId] = useState(
        STICKER_PACKS[0].id
    );
    const [onlineIds, setOnlineIds] = useState([]);
    const [statusMap, setStatusMap] = useState({});
    const [myStatus, setMyStatus] = useState(readStoredStatus);

    const [groupOpen, setGroupOpen] = useState(false);
    const [groupTitle, setGroupTitle] = useState('');
    const [groupMembers, setGroupMembers] = useState([]);
    const [groupAvatarFile, setGroupAvatarFile] = useState(
        null
    );
    const [groupAvatarPreview, setGroupAvatarPreview] =
        useState(null);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const [profileUser, setProfileUser] = useState(null);
    const [profileAvatarUploading, setProfileAvatarUploading] =
        useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [reactionEmojis, setReactionEmojis] = useState(
        DEFAULT_REACTION_EMOJIS
    );
    const [bitrixModal, setBitrixModal] = useState(null);
    const [bitrixTitle, setBitrixTitle] = useState('');
    const [bitrixCreating, setBitrixCreating] = useState(false);
    const [chatMode, setChatMode] = useState(() =>
        loadChatUi().mode === 'mini' ? 'mini' : 'full'
    );
    const [panelPos, setPanelPos] = useState(() => {
        const saved = loadChatUi();
        if (
            Number.isFinite(saved.x) &&
            Number.isFinite(saved.y)
        ) {
            return { x: saved.x, y: saved.y };
        }
        return defaultMiniPos();
    });
    const [panelSize, setPanelSize] = useState(() => {
        const saved = loadChatUi();
        const fallback = defaultMiniSize();
        return {
            w: Number.isFinite(saved.w)
                ? saved.w
                : fallback.w,
            h: Number.isFinite(saved.h)
                ? saved.h
                : fallback.h
        };
    });
    const [fabPos, setFabPos] = useState(() => {
        const saved = loadChatUi();
        if (
            Number.isFinite(saved.fabX) &&
            Number.isFinite(saved.fabY)
        ) {
            return { x: saved.fabX, y: saved.fabY };
        }
        return defaultFabPos();
    });

    const bottomRef = useRef(null);
    const messagesBoxRef = useRef(null);
    const panelRef = useRef(null);
    const fabRef = useRef(null);
    const dragStateRef = useRef(null);
    const fabMovedRef = useRef(false);
    const socketRef = useRef(null);
    const activeRoomIdRef = useRef(null);
    const openRef = useRef(false);
    const seenMsgIds = useRef(new Set());
    const fileInputRef = useRef(null);
    const dragDepthRef = useRef(0);
    const myStatusRef = useRef(myStatus);

    useEffect(() => {
        myStatusRef.current = myStatus;
    }, [myStatus]);

    useEffect(() => {
        activeRoomIdRef.current = activeRoomId;
    }, [activeRoomId]);

    useEffect(() => {
        openRef.current = open;
    }, [open]);

    const activeRoom = useMemo(
        () => rooms.find(r => r.id === activeRoomId) || null,
        [rooms, activeRoomId]
    );

    const unreadTotal = useMemo(
        () =>
            rooms.reduce(
                (sum, room) => sum + Number(room.unread || 0),
                0
            ),
        [rooms]
    );

    const loadRooms = useCallback(async () => {
        try {
            const { data } = await axios.get(
                '/api/chat/rooms',
                { headers: authHeaders() }
            );
            setRooms(data.rooms || []);
        } catch (_) {
            /* ignore */
        } finally {
            setLoadingRooms(false);
        }
    }, []);

    const loadDirectory = useCallback(async () => {
        try {
            const { data } = await axios.get(
                '/api/chat/directory',
                { headers: authHeaders() }
            );
            setDirectory(data.users || []);
            if (Array.isArray(data.online_ids)) {
                setOnlineIds(data.online_ids);
            }
            if (
                data.statuses &&
                typeof data.statuses === 'object'
            ) {
                setStatusMap(data.statuses);
            }
        } catch (_) {
            /* ignore */
        }
    }, []);

    const scrollMessagesToBottom = useCallback(
        (behavior = 'auto') => {
            const box = messagesBoxRef.current;
            if (box) {
                if (behavior === 'smooth') {
                    box.scrollTo({
                        top: box.scrollHeight,
                        behavior: 'smooth'
                    });
                } else {
                    box.scrollTop = box.scrollHeight;
                }
                return;
            }
            bottomRef.current?.scrollIntoView({
                behavior:
                    behavior === 'smooth' ? 'smooth' : 'auto',
                block: 'end'
            });
        },
        []
    );

    const loadMessages = useCallback(async roomId => {
        if (!roomId) return;
        setLoadingMessages(true);
        try {
            const { data } = await axios.get(
                `/api/chat/rooms/${roomId}/messages`,
                {
                    headers: authHeaders(),
                    params: { limit: 80 }
                }
            );
            const sorted = sortMessagesAsc(data.messages || []);
            setMessages(sorted);
            if (Array.isArray(data.reaction_emojis) && data.reaction_emojis.length) {
                setReactionEmojis(data.reaction_emojis);
            }
            sorted.forEach(m => {
                if (m?.id) seenMsgIds.current.add(m.id);
            });
            await axios.post(
                `/api/chat/rooms/${roomId}/read`,
                {},
                { headers: authHeaders() }
            );
            setRooms(prev =>
                prev.map(r =>
                    r.id === roomId
                        ? { ...r, unread: 0 }
                        : r
                )
            );
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Нет доступа к сообщениям'
            );
            setMessages([]);
        } finally {
            setLoadingMessages(false);
        }
    }, []);

    const openPanel = useCallback((roomId = null) => {
        setOpen(true);
        setHasAlert(false);
        if (roomId) {
            setActiveRoomId(roomId);
        }
    }, []);

    const closePanel = useCallback(() => {
        setOpen(false);
        setPickerTab(null);
        setHasAlert(false);
    }, []);

    const toggleChatMode = useCallback(() => {
        setChatMode(prev => {
            const next = prev === 'mini' ? 'full' : 'mini';
            if (next === 'mini') {
                setPanelPos(() => {
                    const saved = loadChatUi();
                    const point =
                        Number.isFinite(saved.x) &&
                        Number.isFinite(saved.y)
                            ? { x: saved.x, y: saved.y }
                            : defaultMiniPos();
                    const size =
                        Number.isFinite(saved.w) &&
                        Number.isFinite(saved.h)
                            ? { w: saved.w, h: saved.h }
                            : defaultMiniSize();
                    setPanelSize(size);
                    saveChatUi({
                        mode: next,
                        ...point,
                        ...size
                    });
                    return point;
                });
            } else {
                saveChatUi({ mode: next });
            }
            return next;
        });
    }, []);

    const onPanelDragStart = event => {
        if (chatMode !== 'mini' || !open) return;
        if (
            event.target.closest(
                'button, .ant-select, input, textarea, a, .emp-chat-panel__resize'
            )
        ) {
            return;
        }
        const panel = panelRef.current;
        if (!panel) return;
        const rect = panel.getBoundingClientRect();
        dragStateRef.current = {
            type: 'panel',
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            height: rect.height
        };
        panel.classList.add('emp-chat-panel--dragging');
        event.preventDefault();
    };

    const onPanelResizeStart = event => {
        if (chatMode !== 'mini' || !open) return;
        const panel = panelRef.current;
        if (!panel) return;
        const rect = panel.getBoundingClientRect();
        dragStateRef.current = {
            type: 'resize',
            startX: event.clientX,
            startY: event.clientY,
            startW: rect.width,
            startH: rect.height,
            left: rect.left,
            top: rect.top
        };
        panel.classList.add('emp-chat-panel--resizing');
        event.preventDefault();
        event.stopPropagation();
    };

    const onFabPointerDown = event => {
        if (open) return;
        const fab = fabRef.current;
        if (!fab) return;
        const rect = fab.getBoundingClientRect();
        fabMovedRef.current = false;
        dragStateRef.current = {
            type: 'fab',
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            height: rect.height,
            startX: event.clientX,
            startY: event.clientY
        };
        fab.classList.add('emp-chat-fab--dragging');
        event.preventDefault();
    };

    useEffect(() => {
        const onMove = event => {
            const drag = dragStateRef.current;
            if (!drag) return;

            if (drag.type === 'panel') {
                const maxX = Math.max(
                    0,
                    window.innerWidth - drag.width
                );
                const maxY = Math.max(
                    0,
                    window.innerHeight - drag.height
                );
                const x = Math.min(
                    maxX,
                    Math.max(0, event.clientX - drag.offsetX)
                );
                const y = Math.min(
                    maxY,
                    Math.max(0, event.clientY - drag.offsetY)
                );
                setPanelPos({ x, y });
                return;
            }

            if (drag.type === 'resize') {
                const dx = event.clientX - drag.startX;
                const dy = event.clientY - drag.startY;
                const maxW = Math.max(
                    MIN_PANEL_W,
                    window.innerWidth - drag.left - 8
                );
                const maxH = Math.max(
                    MIN_PANEL_H,
                    window.innerHeight - drag.top - 8
                );
                const w = Math.min(
                    maxW,
                    Math.max(MIN_PANEL_W, drag.startW + dx)
                );
                const h = Math.min(
                    maxH,
                    Math.max(MIN_PANEL_H, drag.startH + dy)
                );
                setPanelSize({ w, h });
                return;
            }

            if (drag.type === 'fab') {
                if (
                    Math.abs(event.clientX - drag.startX) >
                        4 ||
                    Math.abs(event.clientY - drag.startY) > 4
                ) {
                    fabMovedRef.current = true;
                }
                const maxX = Math.max(
                    0,
                    window.innerWidth - drag.width
                );
                const maxY = Math.max(
                    0,
                    window.innerHeight - drag.height
                );
                const x = Math.min(
                    maxX,
                    Math.max(0, event.clientX - drag.offsetX)
                );
                const y = Math.min(
                    maxY,
                    Math.max(0, event.clientY - drag.offsetY)
                );
                setFabPos({ x, y });
            }
        };

        const onUp = () => {
            const drag = dragStateRef.current;
            if (!drag) return;
            dragStateRef.current = null;
            panelRef.current?.classList.remove(
                'emp-chat-panel--dragging'
            );
            panelRef.current?.classList.remove(
                'emp-chat-panel--resizing'
            );
            fabRef.current?.classList.remove(
                'emp-chat-fab--dragging'
            );

            if (drag.type === 'panel') {
                setPanelPos(pos => {
                    saveChatUi({
                        mode: 'mini',
                        x: pos.x,
                        y: pos.y
                    });
                    return pos;
                });
            } else if (drag.type === 'resize') {
                setPanelSize(size => {
                    saveChatUi({
                        mode: 'mini',
                        w: size.w,
                        h: size.h
                    });
                    return size;
                });
            } else if (drag.type === 'fab') {
                setFabPos(pos => {
                    saveChatUi({
                        fabX: pos.x,
                        fabY: pos.y
                    });
                    return pos;
                });
            }
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener(
                'pointercancel',
                onUp
            );
        };
    }, []);

    useEffect(() => {
        if (!canUseStaffChat(me?.role_id)) {
            return undefined;
        }
        loadRooms();
        loadDirectory();

        const onOpen = event => {
            openPanel(event?.detail?.roomId || null);
        };
        window.addEventListener('crm-chat-open', onOpen);
        return () =>
            window.removeEventListener(
                'crm-chat-open',
                onOpen
            );
    }, [loadRooms, loadDirectory, me?.role_id, openPanel]);

    useEffect(() => {
        if (location.pathname === '/chat') {
            openPanel();
        }
    }, [location.pathname, openPanel]);

    useEffect(() => {
        if (open && activeRoomId) {
            loadMessages(activeRoomId);
            setReplyTo(null);
            socketRef.current?.emit(
                'chat:join',
                activeRoomId
            );
        }
    }, [activeRoomId, open, loadMessages]);

    useLayoutEffect(() => {
        if (!open || !activeRoomId || loadingMessages) {
            return;
        }
        scrollMessagesToBottom('auto');
        const t1 = window.setTimeout(
            () => scrollMessagesToBottom('auto'),
            50
        );
        const t2 = window.setTimeout(
            () => scrollMessagesToBottom('auto'),
            200
        );
        return () => {
            window.clearTimeout(t1);
            window.clearTimeout(t2);
        };
    }, [
        messages,
        activeRoomId,
        open,
        loadingMessages,
        scrollMessagesToBottom
    ]);

    useEffect(() => {
        if (!canUseStaffChat(me?.role_id)) {
            return undefined;
        }
        const token = localStorage.getItem('token');
        if (!token) return undefined;

        const socket = io({
            path: '/api/socket.io',
            auth: {
                token,
                status: myStatusRef.current
            },
            transports: ['websocket', 'polling']
        });
        socketRef.current = socket;

        socket.on('connect', () => {
            socket.emit('chat:status', {
                status: myStatusRef.current
            });
        });

        socket.on('chat:presence', payload => {
            setOnlineIds(payload?.online_ids || []);
            if (
                payload?.statuses &&
                typeof payload.statuses === 'object'
            ) {
                setStatusMap(payload.statuses);
            }
        });

        socket.on('chat:message', ({ message: msg }) => {
            if (!msg?.id) return;
            if (seenMsgIds.current.has(msg.id)) {
                return;
            }
            seenMsgIds.current.add(msg.id);

            const isMine =
                Number(msg.sender_id) === meId;
            const isActiveRoom =
                Number(msg.room_id) ===
                Number(activeRoomIdRef.current);
            const panelOpen = openRef.current;

            if (panelOpen && isActiveRoom) {
                setMessages(prev => {
                    if (
                        prev.some(
                            m => Number(m.id) === Number(msg.id)
                        )
                    ) {
                        return prev;
                    }
                    return sortMessagesAsc([...prev, msg]);
                });
                if (!isMine) {
                    axios
                        .post(
                            `/api/chat/rooms/${msg.room_id}/read`,
                            {},
                            { headers: authHeaders() }
                        )
                        .catch(() => {});
                }
            }

            setRooms(prev => {
                const roomExists = prev.some(
                    r => r.id === msg.room_id
                );
                const next = roomExists
                    ? prev.map(r => {
                          if (r.id !== msg.room_id) {
                              return r;
                          }
                          const viewing =
                              panelOpen &&
                              Number(r.id) ===
                                  Number(
                                      activeRoomIdRef.current
                                  );
                          return {
                              ...r,
                              last_message_at:
                                  msg.created_at,
                              last_message_preview:
                                  messagePreview(msg) ||
                                  r.last_message_preview,
                              unread: viewing
                                  ? 0
                                  : (r.unread || 0) +
                                    (isMine ? 0 : 1)
                          };
                      })
                    : prev;
                return [...next].sort((a, b) => {
                    const ta = new Date(
                        a.last_message_at || 0
                    ).getTime();
                    const tb = new Date(
                        b.last_message_at || 0
                    ).getTime();
                    return tb - ta;
                });
            });

            if (!isMine) {
                const viewing =
                    panelOpen && isActiveRoom;
                if (!viewing) {
                    setHasAlert(true);
                    playChatMessageSound();
                    showBrowserNotification({
                        title:
                            msg.sender?.name ||
                            'Новое сообщение',
                        body: messagePreview(msg) || 'Вложение',
                        tag: `chat-${msg.room_id}`,
                        onClick: () =>
                            openPanel(msg.room_id)
                    });
                    loadRooms();
                }
            }
        });

        socket.on('chat:read', payload => {
            const at = payload?.at
                ? new Date(payload.at).getTime()
                : Date.now();
            const readerId = Number(payload?.user_id);
            if (readerId === meId) return;

            setMessages(prev =>
                prev.map(m => {
                    if (
                        Number(m.sender_id) !== meId ||
                        Number(m.room_id) !==
                            Number(payload.room_id)
                    ) {
                        return m;
                    }
                    if (
                        new Date(m.created_at).getTime() >
                        at
                    ) {
                        return m;
                    }
                    const nextCount = Math.max(
                        Number(m.read_count || 0) + 1,
                        1
                    );
                    return {
                        ...m,
                        is_read: true,
                        read_count: nextCount
                    };
                })
            );
        });

        socket.on('chat:reaction', payload => {
            const messageId = Number(payload?.message_id);
            if (!messageId) return;
            setMessages(prev =>
                prev.map(m =>
                    Number(m.id) === messageId
                        ? {
                              ...m,
                              reactions:
                                  payload.reactions || []
                          }
                        : m
                )
            );
        });

        const poll = setInterval(() => {
            loadRooms();
        }, 15000);

        return () => {
            clearInterval(poll);
            socket.disconnect();
            socketRef.current = null;
        };
    }, [loadRooms, meId, me?.role_id, openPanel]);

    const dialogItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        const onlineSet = new Set(onlineIds.map(Number));
        const dmByUser = new Map();
        const items = [];

        rooms.forEach(room => {
            if (room.type === 'group') {
                items.push({
                    key: `room-${room.id}`,
                    kind: 'group',
                    room,
                    user: null,
                    title: room.title,
                    preview: room.tm_task_id
                        ? room.last_message_preview ||
                          `Задача #${room.tm_task_id}`
                        : room.last_message_preview ||
                          'Группа',
                    lastAt: room.last_message_at
                        ? new Date(
                              room.last_message_at
                          ).getTime()
                        : 0,
                    unread: room.unread || 0,
                    avatar: room.avatar_url || null,
                    online: false,
                    tmTaskId: room.tm_task_id || null
                });
                return;
            }
            const peer = room.members?.[0];
            if (peer?.id) {
                dmByUser.set(Number(peer.id), room);
            }
        });

        directory.forEach(u => {
            const room = dmByUser.get(Number(u.id));
            const online =
                onlineSet.has(Number(u.id)) ||
                Boolean(u.online);
            const status =
                u.status && u.status !== 'offline'
                    ? u.status
                    : peerStatus(statusMap, u.id);
            items.push({
                key: room
                    ? `room-${room.id}`
                    : `user-${u.id}`,
                kind: 'direct',
                room: room || null,
                user: u,
                title: u.name,
                preview: room?.last_message_preview
                    ? room.last_message_preview
                    : statusText(online, status),
                lastAt: room?.last_message_at
                    ? new Date(room.last_message_at).getTime()
                    : 0,
                unread: room?.unread || 0,
                avatar: u.avatar_url,
                online,
                status
            });
        });

        rooms
            .filter(r => r.type === 'direct')
            .forEach(room => {
                const peerId = Number(
                    room.members?.[0]?.id
                );
                if (
                    peerId &&
                    !directory.some(
                        u => Number(u.id) === peerId
                    )
                ) {
                    items.push({
                        key: `room-${room.id}`,
                        kind: 'direct',
                        room,
                        user: room.members?.[0] || null,
                        title: room.title,
                        preview:
                            room.last_message_preview ||
                            '',
                        lastAt: room.last_message_at
                            ? new Date(
                                  room.last_message_at
                              ).getTime()
                            : 0,
                        unread: room.unread || 0,
                        avatar:
                            room.members?.[0]
                                ?.avatar_url || null,
                        online: false,
                        status: 'offline'
                    });
                }
            });

        return items
            .filter(
                item =>
                    !q ||
                    `${item.title} ${item.preview}`
                        .toLowerCase()
                        .includes(q)
            )
            .sort((a, b) => {
                if (a.lastAt !== b.lastAt) {
                    return b.lastAt - a.lastAt;
                }
                if (a.online !== b.online) {
                    return Number(b.online) - Number(a.online);
                }
                return String(a.title).localeCompare(
                    String(b.title),
                    'ru'
                );
            });
    }, [rooms, directory, onlineIds, statusMap, search]);

    const onlineCount = directory.filter(u => {
        const onlineSet = new Set(onlineIds.map(Number));
        return onlineSet.has(Number(u.id)) || u.online;
    }).length;

    const addPendingFiles = useCallback(list => {
        const incoming = Array.from(list || []).filter(
            Boolean
        );
        if (!incoming.length) return;

        setPendingFiles(prev => {
            const next = [...prev];
            incoming.forEach((raw, idx) => {
                if (next.length >= MAX_CHAT_FILES) {
                    message.warning(
                        `Не больше ${MAX_CHAT_FILES} файлов за раз`
                    );
                    return;
                }
                const named =
                    raw.name &&
                    raw.name !== 'image.png' &&
                    raw.name !== 'blob'
                        ? raw
                        : new File(
                              [raw],
                              `скриншот-${Date.now()}-${idx}.png`,
                              {
                                  type:
                                      raw.type ||
                                      'image/png'
                              }
                          );
                if (BLOCKED_FILE_RE.test(named.name)) {
                    message.error(
                        `Файл «${named.name}» запрещён`
                    );
                    return;
                }
                if (named.size > MAX_CHAT_FILE_BYTES) {
                    message.error(
                        `«${named.name}» больше 25 МБ`
                    );
                    return;
                }
                const preview = /^image\//i.test(
                    named.type || ''
                )
                    ? URL.createObjectURL(named)
                    : null;
                next.push({
                    id: `${Date.now()}-${Math.random()
                        .toString(16)
                        .slice(2)}`,
                    file: named,
                    preview
                });
            });
            return next;
        });
    }, []);

    const removePendingFile = id => {
        setPendingFiles(prev => {
            const item = prev.find(p => p.id === id);
            if (item?.preview) {
                URL.revokeObjectURL(item.preview);
            }
            return prev.filter(p => p.id !== id);
        });
    };

    const clearPendingFiles = () => {
        setPendingFiles(prev => {
            prev.forEach(p => {
                if (p.preview) URL.revokeObjectURL(p.preview);
            });
            return [];
        });
    };

    const collectClipboardFiles = clipboardData => {
        if (!clipboardData) return [];
        const out = [];
        const items = clipboardData.items;
        if (items) {
            for (let i = 0; i < items.length; i += 1) {
                const item = items[i];
                if (item.kind === 'file') {
                    const f = item.getAsFile();
                    if (f) out.push(f);
                }
            }
        }
        if (!out.length && clipboardData.files?.length) {
            return Array.from(clipboardData.files);
        }
        return out;
    };

    const onPaste = e => {
        const files = collectClipboardFiles(e.clipboardData);
        if (!files.length) return;
        e.preventDefault();
        e.stopPropagation();
        addPendingFiles(files);
    };

    const onDragEnter = e => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        setDragging(true);
    };

    const onDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
    };

    const onDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(
            0,
            dragDepthRef.current - 1
        );
        if (dragDepthRef.current === 0) {
            setDragging(false);
        }
    };

    const onDrop = e => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setDragging(false);
        const files = Array.from(
            e.dataTransfer?.files || []
        );
        if (files.length) addPendingFiles(files);
    };

    const appendMessages = rows => {
        const list = (rows || []).filter(Boolean);
        if (!list.length) return;
        setMessages(prev => {
            const seen = new Set(prev.map(m => m.id));
            const extra = [];
            list.forEach(m => {
                if (!m?.id || seen.has(m.id)) return;
                seen.add(m.id);
                seenMsgIds.current.add(m.id);
                extra.push(m);
            });
            return extra.length
                ? sortMessagesAsc([...prev, ...extra])
                : prev;
        });
    };

    const toggleReaction = async (msgId, emoji) => {
        if (!activeRoomId || !msgId || !emoji) return;
        try {
            const { data } = await axios.post(
                `/api/chat/rooms/${activeRoomId}/messages/${msgId}/reactions`,
                { emoji },
                { headers: authHeaders() }
            );
            setMessages(prev =>
                prev.map(m =>
                    Number(m.id) === Number(msgId)
                        ? {
                              ...m,
                              reactions: data.reactions || []
                          }
                        : m
                )
            );
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось поставить реакцию'
            );
        }
    };

    const openBitrixTaskModal = msg => {
        const preview = messagePreviewText(msg).slice(0, 120);
        setBitrixTitle(preview || 'Задача из чата');
        setBitrixModal(msg);
    };

    const createBitrixTask = async () => {
        if (!activeRoomId || !bitrixModal?.id) return;
        const title = String(bitrixTitle || '').trim();
        if (!title) {
            message.warning('Укажите название задачи');
            return;
        }
        setBitrixCreating(true);
        try {
            const { data } = await axios.post(
                `/api/chat/rooms/${activeRoomId}/messages/${bitrixModal.id}/bitrix-task`,
                { title },
                { headers: authHeaders() }
            );
            setBitrixModal(null);
            message.success(
                data?.task_id
                    ? `Задача Bitrix #${data.task_id} создана`
                    : 'Задача Bitrix создана'
            );
            if (data?.url) {
                window.open(data.url, '_blank', 'noopener');
            }
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось создать задачу Bitrix'
            );
        } finally {
            setBitrixCreating(false);
        }
    };

    const send = async () => {
        const body = draft.trim();
        if (
            (!body && !pendingFiles.length) ||
            !activeRoomId ||
            sending
        ) {
            return;
        }
        setSending(true);
        try {
            let data;
            if (pendingFiles.length) {
                const form = new FormData();
                if (body) form.append('body', body);
                if (replyTo?.id) {
                    form.append('reply_to_id', String(replyTo.id));
                }
                pendingFiles.forEach(p => {
                    form.append(
                        'files',
                        p.file,
                        p.file.name
                    );
                });
                const res = await axios.post(
                    `/api/chat/rooms/${activeRoomId}/messages`,
                    form,
                    { headers: authHeaders() }
                );
                data = res.data;
            } else {
                const res = await axios.post(
                    `/api/chat/rooms/${activeRoomId}/messages`,
                    {
                        body,
                        reply_to_id: replyTo?.id || undefined
                    },
                    { headers: authHeaders() }
                );
                data = res.data;
            }
            setDraft('');
            setReplyTo(null);
            setPickerTab(null);
            clearPendingFiles();
            appendMessages(
                data.messages ||
                    (data.message ? [data.message] : [])
            );
            loadRooms();
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось отправить'
            );
        } finally {
            setSending(false);
        }
    };

    const startDirect = async userId => {
        try {
            const { data } = await axios.post(
                '/api/chat/direct',
                { user_id: userId },
                { headers: authHeaders() }
            );
            await loadRooms();
            if (data.room?.id) {
                setActiveRoomId(data.room.id);
                socketRef.current?.emit(
                    'chat:join',
                    data.room.id
                );
            }
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось открыть чат'
            );
        }
    };

    const sendSticker = async (packId, stickerId) => {
        if (!activeRoomId) {
            message.warning('Сначала выберите чат');
            return;
        }
        if (sending) return;
        setSending(true);
        try {
            const { data } = await axios.post(
                `/api/chat/rooms/${activeRoomId}/messages`,
                {
                    body: `${packId}:${stickerId}`,
                    msg_type: 'sticker'
                },
                { headers: authHeaders() }
            );
            setPickerTab(null);
            if (data.message) {
                seenMsgIds.current.add(data.message.id);
                setMessages(prev => {
                    if (
                        prev.some(
                            m => m.id === data.message.id
                        )
                    ) {
                        return prev;
                    }
                    return sortMessagesAsc([
                        ...prev,
                        data.message
                    ]);
                });
            }
            loadRooms();
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось отправить стикер'
            );
        } finally {
            setSending(false);
        }
    };

    const createGroup = async () => {
        if (!groupTitle.trim() || !groupMembers.length) {
            message.warning(
                'Укажите название и участников'
            );
            return;
        }
        try {
            const { data } = await axios.post(
                '/api/chat/groups',
                {
                    title: groupTitle.trim(),
                    member_ids: groupMembers
                },
                { headers: authHeaders() }
            );
            if (groupAvatarFile && data.room?.id) {
                const formData = new FormData();
                formData.append('avatar', groupAvatarFile);
                await axios.post(
                    `/api/chat/rooms/${data.room.id}/avatar`,
                    formData,
                    { headers: authHeaders() }
                );
            }
            setGroupOpen(false);
            setGroupTitle('');
            setGroupMembers([]);
            setGroupAvatarFile(null);
            setGroupAvatarPreview(null);
            await loadRooms();
            if (data.room?.id) {
                openPanel(data.room.id);
            }
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось создать группу'
            );
        }
    };

    const uploadActiveGroupAvatar = async file => {
        if (!activeRoomId || activeRoom?.type !== 'group') {
            return false;
        }
        try {
            const formData = new FormData();
            formData.append('avatar', file);
            const { data } = await axios.post(
                `/api/chat/rooms/${activeRoomId}/avatar`,
                formData,
                { headers: authHeaders() }
            );
            if (data.room) {
                setRooms(prev =>
                    prev.map(r =>
                        r.id === data.room.id
                            ? { ...r, ...data.room }
                            : r
                    )
                );
            }
            message.success('Аватар группы обновлён');
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    'Не удалось загрузить аватар'
            );
        }
        return false;
    };

    const onEmoji = emojiData => {
        setDraft(prev => `${prev}${emojiData.emoji}`);
    };

    const changeStatus = value => {
        const next =
            value === 'work' || value === 'busy'
                ? value
                : 'online';
        setMyStatus(next);
        try {
            localStorage.setItem(CHAT_STATUS_KEY, next);
        } catch {
            /* ignore */
        }
        socketRef.current?.emit('chat:status', {
            status: next
        });
    };

    const activePeerId = Number(
        activeRoom?.type === 'direct'
            ? activeRoom.members?.[0]?.id
            : 0
    );
    const activePeerOnline =
        Boolean(activePeerId) &&
        (onlineIds.map(Number).includes(activePeerId) ||
            Boolean(
                directory.find(
                    u => Number(u.id) === activePeerId
                )?.online
            ));
    const activePeerStatus = activePeerOnline
        ? peerStatus(statusMap, activePeerId)
        : 'offline';

    const resolveUserProfile = useCallback(
        raw => {
            if (!raw) return null;
            const id = Number(
                typeof raw === 'object' ? raw.id : raw
            );
            if (!Number.isFinite(id) || id <= 0) return null;
            const fromDir = directory.find(
                u => Number(u.id) === id
            );
            const fromMember = (
                activeRoom?.members || []
            ).find(u => Number(u.id) === id);
            const base = {
                ...(typeof raw === 'object' ? raw : {}),
                ...(fromMember || {}),
                ...(fromDir || {}),
                id
            };
            const online =
                onlineIds.map(Number).includes(id) ||
                Boolean(fromDir?.online);
            return {
                ...base,
                name:
                    base.name ||
                    [
                        base.last_name,
                        base.first_name,
                        base.middle_name
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .trim() ||
                    base.login ||
                    `ID ${id}`,
                online,
                status: online
                    ? peerStatus(statusMap, id)
                    : 'offline'
            };
        },
        [directory, activeRoom, onlineIds, statusMap]
    );

    const openUserProfile = useCallback(
        raw => {
            const user = resolveUserProfile(raw);
            if (user) setProfileUser(user);
        },
        [resolveUserProfile]
    );

    const openMyProfile = useCallback(
        event => {
            event?.stopPropagation?.();
            const stored = meFromStorage() || me;
            if (!stored?.id) {
                message.warning('Войдите снова, чтобы сменить фото');
                return;
            }
            openUserProfile({
                ...stored,
                id: Number(stored.id),
                name:
                    [
                        stored.last_name,
                        stored.first_name,
                        stored.middle_name
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .trim() ||
                    stored.login ||
                    'Я',
                online: true,
                status: myStatus
            });
        },
        [me, myStatus, openUserProfile]
    );

    const uploadProfileAvatar = async file => {
        if (!file || !profileUser || Number(profileUser.id) !== meId) {
            return false;
        }

        setProfileAvatarUploading(true);
        try {
            const formData = new FormData();
            formData.append('avatar', file);
            const { data } = await axios.post(
                '/api/users/me/avatar',
                formData,
                { headers: authHeaders() }
            );
            const version = Date.now();
            const nextUrl = resolveAvatarSrc(
                { ...data, id: meId },
                version
            );

            setProfileUser(prev =>
                prev
                    ? {
                          ...prev,
                          avatar_url: nextUrl
                      }
                    : prev
            );
            patchStoredUserAvatar(nextUrl);

            setDirectory(prev =>
                prev.map(user =>
                    Number(user.id) === meId
                        ? { ...user, avatar_url: nextUrl }
                        : user
                )
            );
            setRooms(prev =>
                prev.map(room => ({
                    ...room,
                    members: (room.members || []).map(member =>
                        Number(member.id) === meId
                            ? {
                                  ...member,
                                  avatar_url: nextUrl
                              }
                            : member
                    )
                }))
            );
            setMessages(prev =>
                prev.map(msg =>
                    Number(msg.sender?.id) === meId
                        ? {
                              ...msg,
                              sender: {
                                  ...msg.sender,
                                  avatar_url: nextUrl
                              }
                          }
                        : msg
                )
            );

            window.dispatchEvent(
                new CustomEvent('crm-user-avatar-updated', {
                    detail: {
                        user_id: meId,
                        avatar_url: nextUrl
                    }
                })
            );

            message.success('Фото профиля обновлено');
        } catch (error) {
            message.error(
                error.response?.data?.error ||
                    error.response?.data?.message ||
                    'Не удалось загрузить фото'
            );
        } finally {
            setProfileAvatarUploading(false);
        }

        return false;
    };

    const profileAvatarSrc = profileUser
        ? resolveAvatarSrc(profileUser)
        : null;
    const isOwnProfile =
        profileUser && Number(profileUser.id) === meId;

    if (
        location.pathname === '/login' ||
        !localStorage.getItem('token') ||
        !canUseStaffChat(me?.role_id)
    ) {
        return null;
    }

    const receiptLabel = msg => {
        if (msg.is_read) {
            if (Number(msg.read_total) > 1) {
                return `Прочитано ${msg.read_count} из ${msg.read_total}`;
            }
            return 'Прочитано';
        }
        return 'Отправлено';
    };

    return (
        <>
            {open && chatMode === 'full' ? (
                <div
                    className="emp-chat-overlay"
                    onClick={closePanel}
                />
            ) : null}

            <div
                ref={panelRef}
                className={[
                    'emp-chat-panel',
                    open ? 'emp-chat-panel--open' : '',
                    chatMode === 'mini'
                        ? 'emp-chat-panel--mini'
                        : '',
                    chatMode === 'mini' && panelSize.w < 520
                        ? 'emp-chat-panel--narrow'
                        : ''
                ]
                    .filter(Boolean)
                    .join(' ')}
                style={
                    chatMode === 'mini'
                        ? {
                              left: panelPos.x,
                              top: panelPos.y,
                              width: panelSize.w,
                              height: panelSize.h,
                              right: 'auto',
                              bottom: 'auto'
                          }
                        : undefined
                }
            >
                <header
                    className={
                        chatMode === 'mini'
                            ? 'emp-chat-panel__bar emp-chat-panel__bar--drag'
                            : 'emp-chat-panel__bar'
                    }
                    onPointerDown={onPanelDragStart}
                >
                    <Space>
                        {chatMode === 'mini' ? (
                            <DragOutlined className="emp-chat-panel__drag-icon" />
                        ) : (
                            <MessageOutlined />
                        )}
                        <Title
                            level={5}
                            style={{ margin: 0 }}
                        >
                            {chatMode === 'mini'
                                ? 'Чат'
                                : 'Чат сотрудников'}
                        </Title>
                    </Space>
                    <Space size={4}>
                        <button
                            type="button"
                            className="emp-chat__me-btn"
                            onClick={openMyProfile}
                            onPointerDown={event =>
                                event.stopPropagation()
                            }
                            title="Мой профиль · сменить фото"
                            aria-label="Мой профиль"
                        >
                            <span className="emp-chat__avatar-wrap emp-chat__me-avatar">
                                <Avatar
                                    size={28}
                                    src={
                                        resolveAvatarSrc(
                                            meFromStorage() || me
                                        ) || undefined
                                    }
                                    icon={<UserOutlined />}
                                >
                                    {initials(
                                        [
                                            me?.last_name,
                                            me?.first_name
                                        ]
                                            .filter(Boolean)
                                            .join(' ') ||
                                            me?.login
                                    )}
                                </Avatar>
                                <span
                                    className={statusDotClass(
                                        true,
                                        myStatus
                                    )}
                                />
                                <span className="emp-chat__me-camera">
                                    <CameraOutlined />
                                </span>
                            </span>
                        </button>
                        <Select
                            size="small"
                            value={myStatus}
                            onChange={changeStatus}
                            popupMatchSelectWidth={false}
                            getPopupContainer={() =>
                                document.body
                            }
                            dropdownStyle={{
                                zIndex: CHAT_POPUP_Z
                            }}
                            className="emp-chat__status-select"
                            options={CHAT_STATUSES.map(
                                s => ({
                                    value: s.id,
                                    label: (
                                        <span className="emp-chat__status-opt">
                                            <span
                                                className={`emp-chat__status-dot emp-chat__status-dot--${s.id}`}
                                            />
                                            {s.label}
                                        </span>
                                    )
                                })
                            )}
                        />
                        <Button
                            type="text"
                            size="small"
                            icon={
                                chatMode === 'mini' ? (
                                    <ExpandOutlined />
                                ) : (
                                    <CompressOutlined />
                                )
                            }
                            title={
                                chatMode === 'mini'
                                    ? 'Развернуть'
                                    : 'Мини-окно'
                            }
                            onClick={toggleChatMode}
                        />
                        <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={closePanel}
                            aria-label="Закрыть чат"
                        />
                    </Space>
                </header>

                <div className="emp-chat">
                    <aside className="emp-chat__sidebar">
                        <div className="emp-chat__sidebar-head">
                            <Button
                                size="small"
                                type="primary"
                                icon={<TeamOutlined />}
                                onClick={() => {
                                    loadDirectory();
                                    setGroupOpen(true);
                                }}
                            >
                                Группа
                            </Button>
                            <Text type="secondary">
                                Онлайн {onlineCount}
                            </Text>
                        </div>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Поиск сотрудников и чатов…"
                            value={search}
                            onChange={e =>
                                setSearch(e.target.value)
                            }
                            style={{ marginBottom: 12 }}
                        />
                        <div className="emp-chat__room-list">
                            {loadingRooms &&
                            !dialogItems.length ? (
                                <div className="emp-chat__center">
                                    <Spin />
                                </div>
                            ) : dialogItems.length === 0 ? (
                                <Empty
                                    image={
                                        Empty.PRESENTED_IMAGE_SIMPLE
                                    }
                                    description="Нет сотрудников"
                                />
                            ) : (
                                dialogItems.map(item => {
                                    const isActive =
                                        item.room?.id ===
                                        activeRoomId;
                                    return (
                                        <button
                                            type="button"
                                            key={item.key}
                                            className={
                                                isActive
                                                    ? 'emp-chat__room emp-chat__room--active'
                                                    : 'emp-chat__room'
                                            }
                                            onClick={() => {
                                                if (
                                                    item.room
                                                        ?.id
                                                ) {
                                                    setActiveRoomId(
                                                        item
                                                            .room
                                                            .id
                                                    );
                                                    return;
                                                }
                                                if (
                                                    item.user
                                                        ?.id
                                                ) {
                                                    startDirect(
                                                        item
                                                            .user
                                                            .id
                                                    );
                                                }
                                            }}
                                        >
                                            <Badge
                                                count={
                                                    item.unread ||
                                                    0
                                                }
                                                size="small"
                                                offset={[
                                                    -4, 4
                                                ]}
                                            >
                                                <span
                                                    className="emp-chat__avatar-wrap"
                                                    onClick={e => {
                                                        if (
                                                            item.kind !==
                                                                'direct' ||
                                                            !item.user
                                                                ?.id
                                                        ) {
                                                            return;
                                                        }
                                                        e.stopPropagation();
                                                        openUserProfile(
                                                            item.user
                                                        );
                                                    }}
                                                >
                                                    <Avatar
                                                        size={
                                                            42
                                                        }
                                                        src={
                                                            item.avatar ||
                                                            undefined
                                                        }
                                                        icon={
                                                            item.kind ===
                                                            'group' ? (
                                                                <TeamOutlined />
                                                            ) : (
                                                                <UserOutlined />
                                                            )
                                                        }
                                                        style={{
                                                            backgroundColor:
                                                                item.kind ===
                                                                'group'
                                                                    ? '#2f6fed'
                                                                    : '#5b8c5a',
                                                            cursor:
                                                                item.kind ===
                                                                'direct'
                                                                    ? 'pointer'
                                                                    : undefined
                                                        }}
                                                    >
                                                        {initials(
                                                            item.title
                                                        )}
                                                    </Avatar>
                                                    {item.kind ===
                                                    'direct' ? (
                                                        <span
                                                            className={statusDotClass(
                                                                item.online,
                                                                item.status
                                                            )}
                                                        />
                                                    ) : null}
                                                </span>
                                            </Badge>
                                            <div className="emp-chat__room-meta">
                                                <div className="emp-chat__room-title">
                                                    {
                                                        item.title
                                                    }
                                                </div>
                                                <div className="emp-chat__room-preview">
                                                    {
                                                        item.preview
                                                    }
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </aside>

                    <section
                        className={
                            dragging
                                ? 'emp-chat__thread emp-chat__thread--drop'
                                : 'emp-chat__thread'
                        }
                        onPaste={onPaste}
                        onDragEnter={onDragEnter}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                    >
                        {dragging ? (
                            <div className="emp-chat__drop-hint">
                                Отпустите файлы, чтобы
                                прикрепить
                            </div>
                        ) : null}
                        {!activeRoom ? (
                            <div className="emp-chat__empty">
                                <MessageOutlined
                                    style={{
                                        fontSize: 48
                                    }}
                                />
                                <Title level={4}>
                                    Выберите чат
                                </Title>
                                <Text type="secondary">
                                    Выберите сотрудника
                                    слева или создайте
                                    группу.
                                </Text>
                                <Button
                                    type="primary"
                                    icon={
                                        <TeamOutlined />
                                    }
                                    onClick={() => {
                                        loadDirectory();
                                        setGroupOpen(true);
                                    }}
                                >
                                    Создать группу
                                </Button>
                            </div>
                        ) : (
                            <>
                                <header className="emp-chat__thread-head">
                                    {activeRoom.type ===
                                    'direct' ? (
                                        <button
                                            type="button"
                                            className="emp-chat__thread-peer"
                                            onClick={() =>
                                                openUserProfile(
                                                    activeRoom
                                                        .members?.[0]
                                                )
                                            }
                                        >
                                            <span className="emp-chat__avatar-wrap">
                                                <Avatar
                                                    src={
                                                        activeRoom
                                                            .members?.[0]
                                                            ?.avatar_url
                                                    }
                                                    icon={
                                                        <UserOutlined />
                                                    }
                                                />
                                                <span
                                                    className={statusDotClass(
                                                        activePeerOnline,
                                                        activePeerStatus
                                                    )}
                                                />
                                            </span>
                                            <div>
                                                <div className="emp-chat__thread-title">
                                                    {
                                                        activeRoom.title
                                                    }
                                                </div>
                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        fontSize: 12
                                                    }}
                                                >
                                                    {statusText(
                                                        activePeerOnline,
                                                        activePeerStatus
                                                    )}
                                                </Text>
                                            </div>
                                        </button>
                                    ) : (
                                        <Space>
                                            <span className="emp-chat__avatar-wrap">
                                                <Avatar
                                                    src={
                                                        activeRoom.avatar_url ||
                                                        undefined
                                                    }
                                                    icon={
                                                        <TeamOutlined />
                                                    }
                                                />
                                            </span>
                                            <div>
                                                <div className="emp-chat__thread-title">
                                                    {
                                                        activeRoom.title
                                                    }
                                                </div>
                                                <Text
                                                    type="secondary"
                                                    style={{
                                                        fontSize: 12
                                                    }}
                                                >
                                                    {`${activeRoom.member_count || 0} участников`}
                                                </Text>
                                            </div>
                                        </Space>
                                    )}
                                    {activeRoom.type ===
                                        'group' &&
                                    activeRoom.my_role ===
                                        'admin' ? (
                                        <Upload
                                            accept="image/*"
                                            showUploadList={
                                                false
                                            }
                                            beforeUpload={
                                                uploadActiveGroupAvatar
                                            }
                                        >
                                            <Button
                                                size="small"
                                                icon={
                                                    <UploadOutlined />
                                                }
                                            >
                                                Аватар
                                            </Button>
                                        </Upload>
                                    ) : null}
                                </header>

                                <div
                                    className="emp-chat__messages"
                                    ref={messagesBoxRef}
                                >
                                    {loadingMessages ? (
                                        <div className="emp-chat__center">
                                            <Spin />
                                        </div>
                                    ) : messages.length ===
                                      0 ? (
                                        <Empty description="Начните переписку" />
                                    ) : (
                                        messages.map(
                                            msg => {
                                                const mine =
                                                    Number(
                                                        msg.sender_id
                                                    ) ===
                                                    meId;
                                                if (
                                                    msg.msg_type ===
                                                    'system'
                                                ) {
                                                    return (
                                                        <div
                                                            key={
                                                                msg.id
                                                            }
                                                            className="emp-chat__system"
                                                        >
                                                            {
                                                                msg.body
                                                            }
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div
                                                        key={
                                                            msg.id
                                                        }
                                                        className={
                                                            mine
                                                                ? 'emp-chat__bubble emp-chat__bubble--mine'
                                                                : 'emp-chat__bubble'
                                                        }
                                                    >
                                                        {!mine ? (
                                                            <span className="emp-chat__bubble-avatar">
                                                                <Avatar
                                                                    size={
                                                                        32
                                                                    }
                                                                    src={
                                                                        msg
                                                                            .sender
                                                                            ?.avatar_url
                                                                    }
                                                                    icon={
                                                                        <UserOutlined />
                                                                    }
                                                                    onClick={() =>
                                                                        openUserProfile(
                                                                            msg.sender
                                                                        )
                                                                    }
                                                                />
                                                            </span>
                                                        ) : null}
                                                        <div
                                                            className={
                                                                msg.msg_type ===
                                                                'sticker'
                                                                    ? 'emp-chat__bubble-body emp-chat__bubble-body--sticker'
                                                                    : msg.msg_type ===
                                                                            'image' ||
                                                                        (msg.file &&
                                                                            previewKind(
                                                                                msg.file
                                                                            ) ===
                                                                                'image')
                                                                      ? 'emp-chat__bubble-body emp-chat__bubble-body--image'
                                                                      : 'emp-chat__bubble-body'
                                                            }
                                                        >
                                                            <div className="emp-chat__bubble-actions">
                                                                <button
                                                                    type="button"
                                                                    title="Ответить"
                                                                    onClick={() =>
                                                                        setReplyTo(msg)
                                                                    }
                                                                >
                                                                    <EnterOutlined />
                                                                </button>
                                                                <Dropdown
                                                                    menu={{
                                                                        items: reactionEmojis.map(
                                                                            emoji => ({
                                                                                key: emoji,
                                                                                label: (
                                                                                    <span style={{ fontSize: 18 }}>
                                                                                        {emoji}
                                                                                    </span>
                                                                                ),
                                                                                onClick: () =>
                                                                                    toggleReaction(
                                                                                        msg.id,
                                                                                        emoji
                                                                                    )
                                                                            })
                                                                        )
                                                                    }}
                                                                    trigger={['click']}
                                                                    placement="topRight"
                                                                    getPopupContainer={() =>
                                                                        document.body
                                                                    }
                                                                    popupClassName="emp-chat-popup"
                                                                    overlayClassName="emp-chat-popup"
                                                                    styles={{
                                                                        root: {
                                                                            zIndex: CHAT_POPUP_Z
                                                                        }
                                                                    }}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        title="Реакция"
                                                                    >
                                                                        <SmileOutlined />
                                                                    </button>
                                                                </Dropdown>
                                                                <button
                                                                    type="button"
                                                                    title="Создать задачу Bitrix"
                                                                    onClick={() =>
                                                                        openBitrixTaskModal(msg)
                                                                    }
                                                                >
                                                                    <FileAddOutlined />
                                                                </button>
                                                            </div>
                                                            {!mine ? (
                                                                <button
                                                                    type="button"
                                                                    className="emp-chat__bubble-name"
                                                                    onClick={() =>
                                                                        openUserProfile(
                                                                            msg.sender
                                                                        )
                                                                    }
                                                                >
                                                                    {msg
                                                                        .sender
                                                                        ?.name ||
                                                                        ''}
                                                                </button>
                                                            ) : null}
                                                            {msg.reply_to ? (
                                                                <div className="emp-chat__bubble-reply">
                                                                    <strong>
                                                                        {msg.reply_to.sender_name ||
                                                                            'Сообщение'}
                                                                    </strong>
                                                                    <span>
                                                                        {msg.reply_to.body_preview ||
                                                                            ''}
                                                                    </span>
                                                                </div>
                                                            ) : null}
                                                            {msg.deleted ? (
                                                                <div className="emp-chat__bubble-text">
                                                                    Сообщение
                                                                    удалено
                                                                </div>
                                                            ) : msg.msg_type ===
                                                              'sticker' ? (
                                                                <StickerMedia
                                                                    className="emp-chat__sticker-img"
                                                                    sticker={findSticker(
                                                                        msg.body
                                                                    )}
                                                                />
                                                            ) : (
                                                                <>
                                                                    {msg.msg_type ===
                                                                        'image' &&
                                                                    msg
                                                                        .file
                                                                        ?.url ? (
                                                                        <ChatAuthImage
                                                                            url={
                                                                                msg
                                                                                    .file
                                                                                    .url
                                                                            }
                                                                            alt={
                                                                                msg
                                                                                    .file
                                                                                    .name
                                                                            }
                                                                            file={
                                                                                msg.file
                                                                            }
                                                                            onOpen={
                                                                                setPreviewFile
                                                                            }
                                                                        />
                                                                    ) : null}
                                                                    {msg.msg_type ===
                                                                        'file' &&
                                                                    msg.file ? (
                                                                        previewKind(
                                                                            msg.file
                                                                        ) ===
                                                                        'image' ? (
                                                                            <ChatAuthImage
                                                                                url={
                                                                                    msg
                                                                                        .file
                                                                                        .url
                                                                                }
                                                                                alt={
                                                                                    msg
                                                                                        .file
                                                                                        .name
                                                                                }
                                                                                file={
                                                                                    msg.file
                                                                                }
                                                                                onOpen={
                                                                                    setPreviewFile
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                className="emp-chat__file"
                                                                                onClick={() =>
                                                                                    setPreviewFile(
                                                                                        msg.file
                                                                                    )
                                                                                }
                                                                            >
                                                                                <EyeOutlined />
                                                                                <span>
                                                                                    <span className="emp-chat__file-name">
                                                                                        {
                                                                                            msg
                                                                                                .file
                                                                                                .name
                                                                                        }
                                                                                    </span>
                                                                                    <span className="emp-chat__file-size">
                                                                                        {formatBytes(
                                                                                            msg
                                                                                                .file
                                                                                                .size
                                                                                        )}
                                                                                        {' · открыть'}
                                                                                    </span>
                                                                                </span>
                                                                            </button>
                                                                        )
                                                                    ) : null}
                                                                    {msg.body ? (
                                                                        <div className="emp-chat__bubble-text">
                                                                            {
                                                                                msg.body
                                                                            }
                                                                        </div>
                                                                    ) : null}
                                                                </>
                                                            )}
                                                            <div className="emp-chat__bubble-time">
                                                                {msg.created_at
                                                                    ? new Date(
                                                                          msg.created_at
                                                                      ).toLocaleString(
                                                                          'ru-RU',
                                                                          {
                                                                              hour: '2-digit',
                                                                              minute: '2-digit',
                                                                              day: '2-digit',
                                                                              month: 'short'
                                                                          }
                                                                      )
                                                                    : ''}
                                                                {mine ? (
                                                                    <span
                                                                        className={
                                                                            msg.is_read
                                                                                ? 'emp-chat__receipt emp-chat__receipt--read'
                                                                                : 'emp-chat__receipt'
                                                                        }
                                                                    >
                                                                        <CheckOutlined />
                                                                        {msg.is_read ? (
                                                                            <CheckOutlined />
                                                                        ) : null}{' '}
                                                                        {receiptLabel(
                                                                            msg
                                                                        )}
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                            {(msg.reactions || [])
                                                                .length ? (
                                                                <div className="emp-chat__reactions">
                                                                    {msg.reactions.map(
                                                                        reaction => (
                                                                            <button
                                                                                type="button"
                                                                                key={`${msg.id}-${reaction.emoji}`}
                                                                                className={
                                                                                    reaction.me
                                                                                        ? 'emp-chat__reaction emp-chat__reaction--me'
                                                                                        : 'emp-chat__reaction'
                                                                                }
                                                                                title={
                                                                                    (reaction.users || [])
                                                                                        .map(u => u.name)
                                                                                        .join(', ') ||
                                                                                    reaction.emoji
                                                                                }
                                                                                onClick={() =>
                                                                                    toggleReaction(
                                                                                        msg.id,
                                                                                        reaction.emoji
                                                                                    )
                                                                                }
                                                                            >
                                                                                <span>{reaction.emoji}</span>
                                                                                <span>{reaction.count}</span>
                                                                            </button>
                                                                        )
                                                                    )}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )
                                    )}
                                    <div ref={bottomRef} />
                                </div>

                                <footer className="emp-chat__composer">
                                    {replyTo ? (
                                        <div className="emp-chat__composer-reply">
                                            <div className="emp-chat__composer-reply-text">
                                                <strong>
                                                    Ответ{' '}
                                                    {replyTo.sender
                                                        ?.name ||
                                                        ''}
                                                </strong>
                                                <span>
                                                    {messagePreviewText(
                                                        replyTo
                                                    ).slice(
                                                        0,
                                                        140
                                                    )}
                                                </span>
                                            </div>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={
                                                    <CloseOutlined />
                                                }
                                                onClick={() =>
                                                    setReplyTo(
                                                        null
                                                    )
                                                }
                                            />
                                        </div>
                                    ) : null}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        hidden
                                        onChange={e => {
                                            addPendingFiles(
                                                e.target
                                                    .files
                                            );
                                            e.target.value =
                                                '';
                                        }}
                                    />
                                    {pendingFiles.length ? (
                                        <div className="emp-chat__pending">
                                            {pendingFiles.map(
                                                item => (
                                                    <div
                                                        key={
                                                            item.id
                                                        }
                                                        className="emp-chat__pending-item"
                                                    >
                                                        {item.preview ? (
                                                            <button
                                                                type="button"
                                                                className="emp-chat__pending-thumb"
                                                                onClick={() =>
                                                                    setPreviewFile(
                                                                        {
                                                                            url: item.preview,
                                                                            name:
                                                                                item
                                                                                    .file
                                                                                    .name ||
                                                                                'Скриншот',
                                                                            mime:
                                                                                item
                                                                                    .file
                                                                                    .type ||
                                                                                'image/png'
                                                                        }
                                                                    )
                                                                }
                                                                title="Открыть"
                                                            >
                                                                <img
                                                                    src={
                                                                        item.preview
                                                                    }
                                                                    alt=""
                                                                />
                                                            </button>
                                                        ) : (
                                                            <FileOutlined />
                                                        )}
                                                        <span>
                                                            {item
                                                                .file
                                                                .name}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="emp-chat__pending-remove"
                                                            onClick={() =>
                                                                removePendingFile(
                                                                    item.id
                                                                )
                                                            }
                                                            aria-label="Убрать файл"
                                                        >
                                                            <CloseCircleFilled />
                                                        </button>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    ) : null}
                                    {pickerTab ===
                                    'emoji' ? (
                                        <div className="emp-chat__picker">
                                            <div className="emp-chat__picker-tabs">
                                                <span className="emp-chat__pack-title">
                                                    Смайлы
                                                </span>
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={
                                                        <CloseOutlined />
                                                    }
                                                    onClick={() =>
                                                        setPickerTab(
                                                            null
                                                        )
                                                    }
                                                />
                                            </div>
                                            <EmojiPicker
                                                onEmojiClick={
                                                    onEmoji
                                                }
                                                theme={
                                                    Theme.LIGHT
                                                }
                                                emojiStyle={
                                                    EmojiStyle.NATIVE
                                                }
                                                height={
                                                    320
                                                }
                                                width={
                                                    340
                                                }
                                                searchPlaceHolder="Поиск эмодзи…"
                                                previewConfig={{
                                                    showPreview: false
                                                }}
                                            />
                                        </div>
                                    ) : null}
                                    {pickerTab ===
                                    'stickers' ? (
                                        <div className="emp-chat__tg-stickers">
                                            <div className="emp-chat__tg-stickers-grid">
                                                {(
                                                    STICKER_PACKS.find(
                                                        p =>
                                                            p.id ===
                                                            activePackId
                                                    ) ||
                                                    STICKER_PACKS[0]
                                                ).stickers.map(
                                                    s => (
                                                        <button
                                                            type="button"
                                                            key={
                                                                s.id
                                                            }
                                                            className="emp-chat__tg-sticker"
                                                            title={
                                                                s.label
                                                            }
                                                            onClick={() =>
                                                                sendSticker(
                                                                    activePackId,
                                                                    s.id
                                                                )
                                                            }
                                                        >
                                                            <StickerMedia
                                                                className="emp-chat__tg-sticker-media"
                                                                sticker={
                                                                    s
                                                                }
                                                            />
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                            <div className="emp-chat__tg-packs">
                                                {STICKER_PACKS.map(
                                                    pack => (
                                                        <button
                                                            type="button"
                                                            key={
                                                                pack.id
                                                            }
                                                            className={
                                                                pack.id ===
                                                                activePackId
                                                                    ? 'emp-chat__tg-pack emp-chat__tg-pack--active'
                                                                    : 'emp-chat__tg-pack'
                                                            }
                                                            title={
                                                                pack.title
                                                            }
                                                            onClick={() =>
                                                                setActivePackId(
                                                                    pack.id
                                                                )
                                                            }
                                                        >
                                                            <img
                                                                src={
                                                                    pack.thumb
                                                                }
                                                                alt={
                                                                    pack.title
                                                                }
                                                            />
                                                        </button>
                                                    )
                                                )}
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={
                                                        <CloseOutlined />
                                                    }
                                                    onClick={() =>
                                                        setPickerTab(
                                                            null
                                                        )
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ) : null}
                                    <div className="emp-chat__composer-tools">
                                        <Button
                                            type="text"
                                            icon={
                                                <PaperClipOutlined />
                                            }
                                            title="Прикрепить файл"
                                            onClick={() =>
                                                fileInputRef.current?.click()
                                            }
                                        />
                                        <Button
                                            type="text"
                                            icon={
                                                <SmileOutlined />
                                            }
                                            onClick={() =>
                                                setPickerTab(
                                                    pickerTab ===
                                                        'emoji'
                                                        ? null
                                                        : 'emoji'
                                                )
                                            }
                                        />
                                        <Button
                                            type="text"
                                            icon={
                                                <GiftOutlined />
                                            }
                                            onClick={() =>
                                                setPickerTab(
                                                    pickerTab ===
                                                        'stickers'
                                                        ? null
                                                        : 'stickers'
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="emp-chat__composer-main">
                                        <TextArea
                                            value={draft}
                                            onChange={e =>
                                                setDraft(
                                                    e.target
                                                        .value
                                                )
                                            }
                                            onPaste={onPaste}
                                            autoSize={{
                                                minRows:
                                                    chatMode ===
                                                    'mini'
                                                        ? 3
                                                        : 2,
                                                maxRows:
                                                    chatMode ===
                                                    'mini'
                                                        ? 10
                                                        : 6
                                            }}
                                            placeholder="Напишите сообщение… Ctrl+V — скриншот"
                                            onPressEnter={e => {
                                                if (
                                                    !e.shiftKey
                                                ) {
                                                    e.preventDefault();
                                                    send();
                                                }
                                            }}
                                        />
                                        <Button
                                            type="primary"
                                            icon={
                                                <SendOutlined />
                                            }
                                            loading={sending}
                                            onClick={send}
                                            aria-label="Отправить"
                                        >
                                            {chatMode ===
                                            'mini'
                                                ? ''
                                                : 'Отправить'}
                                        </Button>
                                    </div>
                                </footer>
                            </>
                        )}
                    </section>
                </div>
                {chatMode === 'mini' ? (
                    <div
                        className="emp-chat-panel__resize"
                        onPointerDown={onPanelResizeStart}
                        title="Изменить размер"
                    />
                ) : null}
            </div>

            <button
                ref={fabRef}
                type="button"
                className={
                    [
                        'emp-chat-fab',
                        hasAlert
                            ? 'emp-chat-fab--alert'
                            : '',
                        open
                            ? 'emp-chat-fab--hidden'
                            : ''
                    ]
                        .filter(Boolean)
                        .join(' ')
                }
                style={{
                    left: fabPos.x,
                    top: fabPos.y,
                    right: 'auto',
                    bottom: 'auto'
                }}
                onPointerDown={onFabPointerDown}
                onClick={() => {
                    if (fabMovedRef.current) {
                        fabMovedRef.current = false;
                        return;
                    }
                    if (open) {
                        closePanel();
                    } else {
                        openPanel();
                    }
                }}
                aria-label="Открыть чат"
                title="Перетащите, чтобы переместить"
            >
                <Badge
                    count={unreadTotal}
                    overflowCount={99}
                    offset={[-2, 6]}
                >
                    <span className="emp-chat-fab__icon">
                        <MessageOutlined />
                    </span>
                </Badge>
                <span className="emp-chat-fab__label">
                    {hasAlert
                        ? 'Новое сообщение'
                        : unreadTotal
                          ? `Чат · ${unreadTotal}`
                          : 'Чат'}
                </span>
            </button>

            {previewFile ? (
                <ChatFilePreview
                    file={previewFile}
                    onClose={() => setPreviewFile(null)}
                />
            ) : null}

            <Modal
                title={isOwnProfile ? 'Мой профиль' : 'Сотрудник'}
                open={Boolean(profileUser)}
                onCancel={() => setProfileUser(null)}
                footer={
                    <Space wrap>
                        {isOwnProfile ? (
                            <Upload
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                showUploadList={false}
                                beforeUpload={uploadProfileAvatar}
                                disabled={profileAvatarUploading}
                            >
                                <Button
                                    type="primary"
                                    icon={<CameraOutlined />}
                                    loading={profileAvatarUploading}
                                >
                                    Сменить фото
                                </Button>
                            </Upload>
                        ) : null}
                        <Button
                            onClick={() => setProfileUser(null)}
                        >
                            Закрыть
                        </Button>
                    </Space>
                }
                destroyOnClose
                centered
                zIndex={33000}
                getContainer={() => document.body}
            >
                {profileUser ? (
                    <div className="emp-chat__profile">
                        <div className="emp-chat__profile-avatar emp-chat__avatar-wrap">
                            {isOwnProfile ? (
                                <Upload
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    showUploadList={false}
                                    beforeUpload={uploadProfileAvatar}
                                    disabled={profileAvatarUploading}
                                    className="emp-chat__profile-upload"
                                >
                                    <button
                                        type="button"
                                        className="emp-chat__profile-photo-btn"
                                        title="Нажмите, чтобы сменить фото"
                                    >
                                        {profileAvatarSrc ? (
                                            <img
                                                src={profileAvatarSrc}
                                                alt={profileUser.name}
                                                className="emp-chat__profile-photo-img"
                                            />
                                        ) : (
                                            <Avatar
                                                size={96}
                                                icon={<UserOutlined />}
                                            >
                                                {initials(
                                                    profileUser.name
                                                )}
                                            </Avatar>
                                        )}
                                        <span className="emp-chat__profile-photo-edit">
                                            <CameraOutlined />{' '}
                                            {profileAvatarUploading
                                                ? 'Загрузка…'
                                                : 'Сменить'}
                                        </span>
                                    </button>
                                </Upload>
                            ) : profileAvatarSrc ? (
                                <Image
                                    src={profileAvatarSrc}
                                    alt={profileUser.name}
                                    width={96}
                                    height={96}
                                    className="emp-chat__profile-photo"
                                    preview={{
                                        mask: (
                                            <span className="emp-chat__profile-photo-mask">
                                                <EyeOutlined />{' '}
                                                Просмотр
                                            </span>
                                        )
                                    }}
                                />
                            ) : (
                                <Avatar
                                    size={96}
                                    icon={<UserOutlined />}
                                >
                                    {initials(profileUser.name)}
                                </Avatar>
                            )}
                            <span
                                className={statusDotClass(
                                    profileUser.online,
                                    profileUser.status
                                )}
                            />
                        </div>
                        {isOwnProfile ? (
                            <Text
                                type="secondary"
                                className="emp-chat__profile-photo-hint"
                            >
                                Нажмите на фото или кнопку «Сменить фото»
                            </Text>
                        ) : profileAvatarSrc ? (
                            <Text
                                type="secondary"
                                className="emp-chat__profile-photo-hint"
                            >
                                Нажмите на фото для просмотра
                            </Text>
                        ) : null}
                        <div className="emp-chat__profile-name">
                            {profileUser.name}
                        </div>
                        <div className="emp-chat__profile-status">
                            {statusText(
                                profileUser.online,
                                profileUser.status
                            )}
                        </div>
                        <div className="emp-chat__profile-rows">
                            {profileUser.login ? (
                                <div className="emp-chat__profile-row">
                                    <span className="emp-chat__profile-label">
                                        Логин
                                    </span>
                                    <span className="emp-chat__profile-value">
                                        {profileUser.login}
                                    </span>
                                </div>
                            ) : null}
                            {profileUser.role_id ? (
                                <div className="emp-chat__profile-row">
                                    <span className="emp-chat__profile-label">
                                        Роль
                                    </span>
                                    <span className="emp-chat__profile-value">
                                        {ROLE_LABELS[
                                            Number(
                                                profileUser.role_id
                                            )
                                        ] ||
                                            `Роль ${profileUser.role_id}`}
                                    </span>
                                </div>
                            ) : null}
                            {profileUser.phone ? (
                                <div className="emp-chat__profile-row">
                                    <span className="emp-chat__profile-label">
                                        Телефон
                                    </span>
                                    <span className="emp-chat__profile-value">
                                        <a
                                            href={`tel:${profileUser.phone}`}
                                        >
                                            {profileUser.phone}
                                        </a>
                                    </span>
                                </div>
                            ) : null}
                            {profileUser.email ? (
                                <div className="emp-chat__profile-row">
                                    <span className="emp-chat__profile-label">
                                        Email
                                    </span>
                                    <span className="emp-chat__profile-value">
                                        <a
                                            href={`mailto:${profileUser.email}`}
                                        >
                                            {profileUser.email}
                                        </a>
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>
                ) : null}
            </Modal>

            <Modal
                title="Новая группа"
                open={groupOpen}
                onCancel={() => {
                    setGroupOpen(false);
                    setGroupAvatarFile(null);
                    setGroupAvatarPreview(null);
                }}
                onOk={createGroup}
                okText="Создать"
                destroyOnClose
                zIndex={30000}
                getContainer={() => document.body}
            >
                <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size="middle"
                >
                    <div className="emp-chat__group-avatar-row">
                        <Avatar
                            size={72}
                            src={
                                groupAvatarPreview ||
                                undefined
                            }
                            icon={<TeamOutlined />}
                            style={{
                                backgroundColor: '#2f6fed'
                            }}
                        />
                        <Upload
                            accept="image/*"
                            showUploadList={false}
                            beforeUpload={file => {
                                setGroupAvatarFile(file);
                                setGroupAvatarPreview(
                                    URL.createObjectURL(
                                        file
                                    )
                                );
                                return false;
                            }}
                        >
                            <Button
                                icon={<UploadOutlined />}
                            >
                                Аватар группы
                            </Button>
                        </Upload>
                    </div>
                    <Input
                        placeholder="Название группы"
                        value={groupTitle}
                        onChange={e =>
                            setGroupTitle(e.target.value)
                        }
                        maxLength={120}
                    />
                    <Select
                        mode="multiple"
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Выберите сотрудников"
                        optionFilterProp="label"
                        value={groupMembers}
                        onChange={setGroupMembers}
                        getPopupContainer={() =>
                            document.body
                        }
                        dropdownStyle={{ zIndex: 31000 }}
                        optionRender={option => (
                            <Space>
                                <Avatar
                                    size={24}
                                    src={
                                        option.data
                                            .avatar ||
                                        undefined
                                    }
                                    icon={
                                        <UserOutlined />
                                    }
                                />
                                <span>
                                    {option.data.name}
                                    <Text
                                        type="secondary"
                                        style={{
                                            marginLeft: 6
                                        }}
                                    >
                                        {option.data.login}
                                    </Text>
                                </span>
                            </Space>
                        )}
                        options={directory.map(u => ({
                            value: u.id,
                            label: `${u.name} (${u.login})`,
                            name: u.name,
                            login: u.login,
                            avatar: u.avatar_url
                        }))}
                    />
                </Space>
            </Modal>

            <Modal
                title="Создать задачу Bitrix"
                open={Boolean(bitrixModal)}
                onCancel={() => setBitrixModal(null)}
                onOk={createBitrixTask}
                confirmLoading={bitrixCreating}
                okText="Создать"
                cancelText="Отмена"
                destroyOnClose
                zIndex={CHAT_POPUP_Z}
                getContainer={() => document.body}
            >
                <Text type="secondary">
                    Задача будет создана в Bitrix из сообщения
                    чата
                </Text>
                <Input
                    style={{ marginTop: 12 }}
                    value={bitrixTitle}
                    onChange={e =>
                        setBitrixTitle(e.target.value)
                    }
                    placeholder="Название задачи"
                    maxLength={200}
                    onPressEnter={createBitrixTask}
                />
                {bitrixModal ? (
                    <div className="emp-chat__bitrix-preview">
                        {messagePreviewText(bitrixModal).slice(
                            0,
                            240
                        )}
                    </div>
                ) : null}
            </Modal>
        </>
    );
};

export default EmployeeChatWidget;
